import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRightCircle, KeyRound, Mail, UserPlus } from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/firebase';
import { getInitialAccessStatus, isBootstrapApprovedEmail } from '../../features/auth/utils/access';
import { bootstrapLocalDevelopmentAccount } from '../../services/workspaceService';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import AuthShell from './AuthShell';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const AUTH_MODE_LOGIN = 'login';
const AUTH_MODE_SIGNUP = 'signup';
const AUTH_MODE_FORGOT = 'forgot-password';
const AUTH_MODE_ORDER = [AUTH_MODE_LOGIN, AUTH_MODE_SIGNUP, AUTH_MODE_FORGOT];
const PANEL_EXIT_MS = 220;
const PANEL_ENTER_MS = 340;

const getModeFromPath = (pathname = '') => {
  if (pathname === '/signup') {
    return AUTH_MODE_SIGNUP;
  }

  if (pathname === '/forgot-password') {
    return AUTH_MODE_FORGOT;
  }

  return AUTH_MODE_LOGIN;
};

const MODE_CONTENT = {
  [AUTH_MODE_LOGIN]: {
    badgeLabel: 'Sign In',
    title: 'Sign in to your workspace',
    subtitle:
      'Sign in and land in the workspace, where you can manage panels, account details, and active sessions.',
  },
  [AUTH_MODE_SIGNUP]: {
    badgeLabel: 'Invite-Only Beta',
    title: 'Create your account',
    subtitle:
      'Create your account and go straight to the workspace. Bootstrap admin emails are auto-approved; all other beta signups may still need access approval.',
  },
  [AUTH_MODE_FORGOT]: {
    badgeLabel: 'Password Reset',
    title: 'Reset your password',
    subtitle: 'Enter the email tied to your workspace account and we’ll send you a secure reset link.',
  },
};

const AuthSurface = ({ modeOverride = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    login,
    signup,
    resetPassword,
    sendVerificationEmail,
    currentUser,
    firebaseDiagnostics,
  } = useAuth();
  const prefersReducedMotion = usePrefersReducedMotion();
  const routeMode = useMemo(() => getModeFromPath(location.pathname), [location.pathname]);
  const mode = modeOverride || routeMode;

  const [emailDraft, setEmailDraft] = useState(() => location.state?.email || '');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupFirstName, setSignupFirstName] = useState('');
  const [signupLastName, setSignupLastName] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [loginError, setLoginError] = useState('');
  const [signupError, setSignupError] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [loadingMode, setLoadingMode] = useState(null);
  const [renderedMode, setRenderedMode] = useState(mode);
  const [panelPhase, setPanelPhase] = useState('idle');
  const [panelDirection, setPanelDirection] = useState('forward');

  const renderedModeRef = useRef(mode);
  const exitTimerRef = useRef(null);
  const enterTimerRef = useRef(null);

  useEffect(() => {
    if (currentUser && mode !== AUTH_MODE_FORGOT) {
      navigate(location.state?.returnTo || '/workspace');
    }
  }, [currentUser, location.state, mode, navigate]);

  useEffect(() => {
    renderedModeRef.current = renderedMode;
  }, [renderedMode]);

  useEffect(
    () => () => {
      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current);
      }
      if (enterTimerRef.current) {
        window.clearTimeout(enterTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const routeEmail = location.state?.email?.trim?.() || '';
    if (!routeEmail) {
      return;
    }

    setEmailDraft((current) => current || routeEmail);
  }, [location.state]);

  const resetTransientFields = () => {
    setLoginPassword('');
    setSignupFirstName('');
    setSignupLastName('');
    setSignupPassword('');
    setSignupPasswordConfirm('');
    setLoginError('');
    setSignupError('');
    setResetError('');
    setResetMessage('');
    setLoadingMode(null);
  };

  useEffect(() => {
    if (mode === renderedModeRef.current) {
      return undefined;
    }

    if (exitTimerRef.current) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    if (enterTimerRef.current) {
      window.clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }

    const currentIndex = AUTH_MODE_ORDER.indexOf(renderedModeRef.current);
    const nextIndex = AUTH_MODE_ORDER.indexOf(mode);
    setPanelDirection(nextIndex > currentIndex ? 'forward' : 'backward');

    if (prefersReducedMotion) {
      resetTransientFields();
      setRenderedMode(mode);
      renderedModeRef.current = mode;
      setPanelPhase('idle');
      return undefined;
    }

    setPanelPhase('exit');

    exitTimerRef.current = window.setTimeout(() => {
      resetTransientFields();
      setRenderedMode(mode);
      renderedModeRef.current = mode;
      setPanelPhase('enter');
      exitTimerRef.current = null;

      enterTimerRef.current = window.setTimeout(() => {
        setPanelPhase('idle');
        enterTimerRef.current = null;
      }, PANEL_ENTER_MS);
    }, PANEL_EXIT_MS);

    return () => {
      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      if (enterTimerRef.current) {
        window.clearTimeout(enterTimerRef.current);
        enterTimerRef.current = null;
      }
    };
  }, [mode, prefersReducedMotion]);

  const handleLoginSubmit = async (event) => {
    event.preventDefault();

    try {
      setLoginError('');
      setLoadingMode(AUTH_MODE_LOGIN);
      await login(emailDraft, loginPassword);
      navigate(location.state?.returnTo || '/workspace');
    } catch (error) {
      setLoginError(`Failed to log in: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingMode(null);
    }
  };

  const handleSignupSubmit = async (event) => {
    event.preventDefault();

    if (signupPassword !== signupPasswordConfirm) {
      setSignupError('Passwords do not match.');
      return;
    }

    const normalizedEmail = emailDraft.trim().toLowerCase();
    const accessStatus = getInitialAccessStatus(normalizedEmail);
    const firstName = signupFirstName.trim();
    const lastName = signupLastName.trim();

    try {
      setSignupError('');
      setLoadingMode(AUTH_MODE_SIGNUP);
      const userCredential = await signup(normalizedEmail, signupPassword);

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        firstName,
        lastName,
        email: normalizedEmail,
        accessStatus,
        createdAt: new Date().toISOString(),
        approvedAt: accessStatus === 'approved' ? new Date().toISOString() : null,
        approvedByUid: accessStatus === 'approved' ? userCredential.user.uid : null,
        approvedByEmail: isBootstrapApprovedEmail(normalizedEmail) ? normalizedEmail : null,
        revokedAt: null,
        revokedByUid: null,
        revokedByEmail: null,
      });

      try {
        if (typeof sendVerificationEmail === 'function') {
          await sendVerificationEmail();
        }
      } catch (verificationError) {
        console.warn('Account created, but verification email could not be sent:', verificationError);
      }

      if (firebaseDiagnostics?.emulatorEnabled) {
        await bootstrapLocalDevelopmentAccount();
      }

      navigate(location.state?.returnTo || '/workspace');
    } catch (error) {
      setSignupError(`Failed to create an account: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingMode(null);
    }
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();

    try {
      setResetMessage('');
      setResetError('');
      setLoadingMode(AUTH_MODE_FORGOT);
      await resetPassword(emailDraft);
      setResetMessage('Check your inbox for password reset instructions.');
    } catch (error) {
      setResetError(`Failed to reset password: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingMode(null);
    }
  };

  const renderFooter = () => {
    if (renderedMode === AUTH_MODE_LOGIN) {
      return (
        <div className="text-center text-sm text-slate-600">
          Don&apos;t have an account?{' '}
          <Link
            to="/signup"
            state={emailDraft ? { email: emailDraft } : undefined}
            className="font-semibold text-slate-800 hover:text-slate-950"
          >
            Create one
          </Link>
        </div>
      );
    }

    if (renderedMode === AUTH_MODE_SIGNUP) {
      return (
        <div className="text-center text-sm text-slate-600">
          Already have an account?{' '}
          <Link
            to="/login"
            state={emailDraft ? { email: emailDraft } : undefined}
            className="font-semibold text-slate-800 hover:text-slate-950"
          >
            Sign in
          </Link>
        </div>
      );
    }

    return (
      <div className="text-center text-sm text-slate-600">
        <Link
          to="/login"
          state={emailDraft ? { email: emailDraft } : undefined}
          className="font-semibold text-slate-800 hover:text-slate-950"
        >
          Back to sign in
        </Link>
      </div>
    );
  };

  const renderPanelContent = () => {
    if (renderedMode === AUTH_MODE_LOGIN) {
      return (
        <form className="space-y-4" onSubmit={handleLoginSubmit}>
          <div className="space-y-3">
            <div>
              <label htmlFor="login-email" className="mb-1 block text-sm font-medium text-slate-700">
                Email
              </label>
              <Input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="attorney@firm.com"
                value={emailDraft}
                onChange={(event) => setEmailDraft(event.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label htmlFor="login-password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  state={emailDraft ? { email: emailDraft } : undefined}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                placeholder="Enter your password"
                value={loginPassword}
                onChange={(event) => setLoginPassword(event.target.value)}
                className="w-full"
              />
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            icon={
              loadingMode === AUTH_MODE_LOGIN ? (
                <ArrowRightCircle size={18} strokeWidth={1.75} />
              ) : (
                <KeyRound size={18} strokeWidth={1.75} />
              )
            }
            isLoading={loadingMode === AUTH_MODE_LOGIN}
            className="w-full"
          >
            {loadingMode === AUTH_MODE_LOGIN ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      );
    }

    if (renderedMode === AUTH_MODE_SIGNUP) {
      return (
        <form className="space-y-4" onSubmit={handleSignupSubmit}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="signup-first-name" className="mb-1 block text-sm font-medium text-slate-700">
                First name
              </label>
              <Input
                id="signup-first-name"
                name="firstName"
                type="text"
                required
                placeholder="First name"
                value={signupFirstName}
                onChange={(event) => setSignupFirstName(event.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="signup-last-name" className="mb-1 block text-sm font-medium text-slate-700">
                Last name
              </label>
              <Input
                id="signup-last-name"
                name="lastName"
                type="text"
                required
                placeholder="Last name"
                value={signupLastName}
                onChange={(event) => setSignupLastName(event.target.value)}
                className="w-full"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label htmlFor="signup-email" className="mb-1 block text-sm font-medium text-slate-700">
                Email
              </label>
              <Input
                id="signup-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="attorney@firm.com"
                value={emailDraft}
                onChange={(event) => setEmailDraft(event.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="signup-password" className="mb-1 block text-sm font-medium text-slate-700">
                Password
              </label>
              <Input
                id="signup-password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                placeholder="Create a password"
                value={signupPassword}
                onChange={(event) => setSignupPassword(event.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="signup-password-confirm" className="mb-1 block text-sm font-medium text-slate-700">
                Confirm password
              </label>
              <Input
                id="signup-password-confirm"
                name="passwordConfirm"
                type="password"
                autoComplete="new-password"
                required
                placeholder="Confirm your password"
                value={signupPasswordConfirm}
                onChange={(event) => setSignupPasswordConfirm(event.target.value)}
                className="w-full"
              />
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            icon={
              loadingMode === AUTH_MODE_SIGNUP ? (
                <ArrowRightCircle size={18} strokeWidth={1.75} />
              ) : (
                <UserPlus size={18} strokeWidth={1.75} />
              )
            }
            isLoading={loadingMode === AUTH_MODE_SIGNUP}
            className="w-full"
          >
            {loadingMode === AUTH_MODE_SIGNUP ? 'Creating account…' : 'Create account'}
          </Button>
        </form>
      );
    }

    return (
      <form className="space-y-4" onSubmit={handleResetSubmit}>
        <div>
          <label htmlFor="forgot-email" className="mb-1 block text-sm font-medium text-slate-700">
            Email
          </label>
          <Input
            id="forgot-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="attorney@firm.com"
            value={emailDraft}
            onChange={(event) => setEmailDraft(event.target.value)}
            className="w-full"
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          icon={
            loadingMode === AUTH_MODE_FORGOT ? (
              <ArrowRightCircle size={18} strokeWidth={1.75} />
            ) : (
              <Mail size={18} strokeWidth={1.75} />
            )
          }
          isLoading={loadingMode === AUTH_MODE_FORGOT}
          className="w-full"
        >
          {loadingMode === AUTH_MODE_FORGOT ? 'Sending reset link…' : 'Send reset link'}
        </Button>
      </form>
    );
  };

  const activeMeta = MODE_CONTENT[renderedMode];
  const activeError =
    renderedMode === AUTH_MODE_LOGIN
      ? loginError
      : renderedMode === AUTH_MODE_SIGNUP
        ? signupError
        : resetError;
  const activeMessage = renderedMode === AUTH_MODE_FORGOT ? resetMessage : '';

  return (
    <AuthShell
      badgeLabel={activeMeta.badgeLabel}
      title={activeMeta.title}
      subtitle={activeMeta.subtitle}
      error={activeError}
      message={activeMessage}
      footer={renderFooter()}
      panelKey={renderedMode}
      panelPhase={panelPhase}
      panelDirection={panelDirection}
    >
      {renderPanelContent()}
    </AuthShell>
  );
};

export default AuthSurface;
